import {
  BASE,
  BRAND,
  BORDER,
  GREY,
  SUCCESS,
} from "../../../../../utils/constants/colors";

// ─── Formatters ────────────────────────────────────────────────────────────────
export const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const formatCredits = (num) => num?.toLocaleString() || "0";

// ─── Constants ─────────────────────────────────────────────────────────────────
export const FEATURE_KEY_LABELS = {
  sequence_builder: "Sequence Builder",
  icp: "ICP",
  personalisation: "Personalized Emails",
  template: "Template",
  reveal_details: "Reveal Details",
};

export const SDR_MEETING_LINK =
  "https://resourcifi.reachiq.ai/auth/meetings/kritika-saxena-1772459849115";

export const SDR_INCLUSIONS = [
  {
    title: "Dedicated SDR",
    desc: "A trained sales rep working full-time on your outbound using our platform",
  },
  {
    title: "Personalized Outreach",
    desc: "Unique emails crafted for every prospect, every step",
  },
  {
    title: "Multi-Channel Sequences",
    desc: "Email, LinkedIn messages, and call scripts, all handled for you",
  },
  {
    title: "ICP Targeting & Data",
    desc: "We identify, reveal, and score your ideal prospects",
  },
  {
    title: "Full Infrastructure Setup",
    desc: "Sender accounts, domains, and sending configuration handled",
  },
  {
    title: "Qualified Meetings Booked",
    desc: "Directly on your calendar with decision-makers",
  },
  {
    title: "CRM & Pipeline Management",
    desc: "All contacts, deals, and activity tracked in your CRM",
  },
  {
    title: "Weekly Reporting",
    desc: "Transparent metrics, campaign performance, and pipeline insights",
  },
];

// ─── Shared styles (used across multiple section components) ───────────────────
export const sharedBillingStyles = {
  contentWrapper: {
    padding: "24px 24px 30px",
    display: "flex",
    flexDirection: "column",
    gap: "30px",
  },
  card: {
    border: `1px solid ${BORDER.DEFAULT}`,
    borderRadius: "12px",
    padding: "22px",
    background: BASE.WHITE,
    boxShadow: `0 12px 24px -24px ${GREY.SECONDARY}`,
    minWidth: 0,
    overflow: "hidden",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "12px",
    marginBottom: "16px",
  },
  badge: {
    padding: "4px 10px",
    borderRadius: "50px",
    fontSize: "10px",
    fontWeight: 700,
    letterSpacing: "0.2px",
    display: "inline-block",
  },
  statBox: {
    background: BASE.BACKGROUND_LIGHT,
    borderRadius: "8px",
    padding: "10px 12px",
    border: `1px solid ${BORDER.LIGHT}`,
    minWidth: 0,
    overflow: "hidden",
  },
  progressTrack: {
    height: "11px",
    background: BORDER.DISABLED,
    borderRadius: "999px",
    border: `1px solid ${BORDER.LIGHT}`,
    overflow: "hidden",
  },
  progressFill: {
    height: "100%",
    background: BRAND.PRIMARY,
    borderRadius: "999px",
    transition: "width 0.22s cubic-bezier(0.4, 0, 0.2, 1)",
  },
  invoiceRow: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto",
    gap: "14px",
    alignItems: "center",
    padding: "12px 14px",
    background: BASE.BACKGROUND_LIGHT,
    borderRadius: "8px",
    border: `1px solid ${BORDER.LIGHT}`,
    minHeight: "58px",
    transition:
      "border-color 0.18s ease, background 0.18s ease, box-shadow 0.18s ease",
  },
  planCard: {
    border: `1px solid ${BORDER.DEFAULT}`,
    borderRadius: "12px",
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    position: "relative",
    overflow: "visible",
    transition: "all 0.3s ease",
    background: BASE.WHITE,
    boxShadow: `0 10px 20px -20px ${GREY.SECONDARY}`,
    cursor: "pointer",
  },
  planCardCurrent: {
    border: `2px solid ${BRAND.PRIMARY}`,
    borderRadius: "12px",
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    position: "relative",
    overflow: "visible",
    background: BASE.WHITE,
    boxShadow: `0 12px 22px -18px ${BRAND.PRIMARY}`,
    cursor: "pointer",
  },
  featuredBadge: {
    position: "absolute",
    top: "-11px",
    left: "50%",
    transform: "translateX(-50%)",
    background: BRAND.PRIMARY,
    color: BASE.WHITE,
    padding: "5px 18px",
    borderRadius: "20px",
    fontSize: "11px",
    fontWeight: 700,
    whiteSpace: "nowrap",
    zIndex: 10,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "300px",
  },
  emptyContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "300px",
    gap: "16px",
    padding: "40px",
  },
  dangerZone: {
    padding: "22px",
    border: `1px solid ${SUCCESS.W_200}`,
    borderRadius: "12px",
  },
  toggleTrack: {
    width: "46px",
    height: "26px",
    borderRadius: "999px",
    position: "relative",
    cursor: "pointer",
    border: `1px solid ${BORDER.DEFAULT}`,
    transition:
      "background 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease",
    flexShrink: 0,
  },
  toggleKnob: {
    width: "20px",
    height: "20px",
    background: BASE.WHITE,
    borderRadius: "50%",
    position: "absolute",
    top: "2px",
    left: "2px",
    transition: "transform 0.18s cubic-bezier(0.4, 0, 0.2, 1)",
    boxShadow: `0 6px 12px -12px ${GREY.PRIMARY}`,
  },
};

// ─── Plan-change classification (mirrors the BE changePlan rule) ────────────────
// Higher catalog credits = higher tier; SDR (unlimited) = top. Same tier -> interval decides
// (->yearly = more commitment = upgrade/immediate; ->monthly = downgrade/period-end).
export const planTierRank = (pkg) => {
  if (!pkg) return 0;
  if (pkg.isSdr) return Number.MAX_SAFE_INTEGER;
  return Number(pkg.credits || 0);
};

// Returns the info a confirmation modal needs.
//   current  = currentSubscription object (has .plan.id, .plan.interval, .status)
//   plan     = the target catalog package (has stripe_price_id, credits, interval, name, isSdr, isTopUp)
//   packages = all catalog packages (to resolve the CURRENT plan's tier)
export const classifyPlanChange = (current, plan, packages = []) => {
  const hasActiveSub = ["active", "trialing"].includes(current?.status);
  if (!hasActiveSub || plan?.isTopUp) {
    return {
      action: "buy",
      title: `Get ${plan?.name || "this plan"}`,
      message: `You'll be taken to secure checkout to start the ${plan?.name || "plan"}.`,
      confirmLabel: "Continue to checkout",
    };
  }
  const currentPkg = packages.find((p) => p.stripe_price_id === current?.plan?.id) || null;
  const curRank = planTierRank(currentPkg);
  const newRank = planTierRank(plan);
  const curInterval = currentPkg?.interval || current?.plan?.interval || "month";
  const newInterval = plan?.interval || "month";

  let action;
  if (newRank > curRank) action = "upgrade";
  else if (newRank < curRank) action = "downgrade";
  else if (curInterval === "month" && newInterval === "year") action = "upgrade";
  else if (curInterval === "year" && newInterval === "month") action = "downgrade";
  else action = "noop";

  const creditsLabel = plan?.isSdr ? "unlimited credits" : `${formatCredits(plan?.credits)} credits`;
  if (action === "upgrade") {
    // Yearly -> yearly upgrade: month-based proration, new plan credits start NEXT month,
    // the yearly renewal date stays the same (mirrors the BE yearly-upgrade path).
    if (curInterval === "year" && newInterval === "year") {
      return {
        action,
        title: `Upgrade to ${plan?.name || "new plan"}`,
        message: `You'll be charged the monthly-prorated difference for your remaining months now. This month stays on your current plan — ${plan?.name || "the new plan"} and its ${creditsLabel} start from next month, and your yearly renewal date stays the same.${current?.cancel_at_period_end ? " Your scheduled cancellation will also be removed." : ""}`,
        confirmLabel: "Confirm upgrade",
      };
    }
    return {
      action,
      title: `Upgrade to ${plan?.name || "new plan"}`,
      message: `This takes effect immediately — your credits are set to ${creditsLabel} now, and you'll be charged the prorated difference for the rest of your current billing period. Your leftover credits from the old plan do not carry over.${current?.cancel_at_period_end ? " Your scheduled cancellation will also be removed." : ""}`,
      confirmLabel: "Confirm upgrade",
    };
  }
  if (action === "downgrade") {
    return {
      action,
      title: `Switch to ${plan?.name || "new plan"}`,
      message: `No change or charge today — your current plan stays active until the end of your billing period. ${plan?.name || "The new plan"} (${creditsLabel}) then takes effect at your next renewal.`,
      confirmLabel: "Schedule the switch",
    };
  }
  return { action: "noop", title: "", message: "", confirmLabel: "" };
};

// Short per-card badge for the plan grid.
export const planChangeBadge = (current, plan, packages = []) => {
  const onCurrentPlan = current?.plan?.id === plan?.stripe_price_id;
  const isTrialing = current?.status === "trialing";
  // A trialing tenant is ON this plan (not shopping) — badge it as current, not upgrade/downgrade.
  const isCurrent = onCurrentPlan && (current?.status === "active" || isTrialing);
  if (isCurrent) return { label: isTrialing ? "Current plan (Trial)" : "Current plan", kind: "current" };
  if (!["active", "trialing"].includes(current?.status)) return null;
  const { action } = classifyPlanChange(current, plan, packages);
  if (action === "upgrade") return { label: "Upgrade", kind: "upgrade" };
  if (action === "downgrade") return { label: "Downgrade", kind: "downgrade" };
  return null;
};
